import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import ItemProgressBar from '../../../shared/ItemProgressBar';
import MilestoneEditor from './MilestoneEditor';
import DragHandleMenu from './DragHandleMenu';
import { MilestonePathIcon } from '../components/MilestonePathModal/MilestonePathIcon';
import { MilestoneFlagIcon } from '../components/MilestoneFlagIcon';
import { openMilestonePathModal } from '../utils/openMilestonePathModal';
import { useTaskNodeHandlers } from './hooks';
import useOnTaskIdentity from './hooks/useOnTaskIdentity';
import { EditableCardName } from '../../shared/CardComponents/MissionCardBody/components';
import {
  getMilestoneMetaLabel,
  getMilestoneAnchor,
  getHostTask,
  MILESTONE_NODE_WIDTH,
  MILESTONE_NODE_HEIGHT,
  isMilestoneNode,
} from '../utils/milestoneUtils';
import { computeMilestoneProgressPercent } from '../components/FlowchartHUD/utils/milestoneHudProgress';
import { shouldMilestoneLabelOnLeft } from '../utils/milestonePlacement';
import { isEstimatedDurationVisible } from '../../shared/CardComponents/MissionCardBody/hooks/useCardDisplaySettings';

const noopConnectionMenu = { openMenu: () => {} };

/**
 * MilestoneNode — passive checkpoint marker on the flow canvas.
 * Attached to a host mission; edges route through it visually.
 */
const MilestoneNode = ({
  task,
  position,
  hostPosition = null,
  isSelected,
  isDragging,
  isAnimating,
  isHoveredFromHUD = false,
  onNodeHoverForHUD,
  onNodeLeaveForHUD,
  allTasks = [],
  nestedSprints = [],
  allSprints = [],
  statuses = [],
  onMouseDown,
  onClick,
  onRename,
  onAnchorChange,
  onDimensionsChange,
  onDelete,
  onCopy,
  onDuplicate,
  cardDisplaySettings = {},
  layoutSurfaceEpoch: _layoutSurfaceEpoch = 0,
}) => {
  const nodeRef = useRef(null);
  const dragHandleMenuRef = useRef(null);
  const [editorOpen, setEditorOpen] = useState(false);

  useOnTaskIdentity(task.id, () => {
    setEditorOpen(false);
  });

  const { handleNodeMouseDown } = useTaskNodeHandlers({
    onMouseDown,
    connectionMenu: noopConnectionMenu,
  });

  const anchor = getMilestoneAnchor(task);
  const host = getHostTask(task, allTasks);

  const labelOnLeft = useMemo(
    () => shouldMilestoneLabelOnLeft(
      position,
      host
        ? {
          ...host,
          _placementPos: hostPosition && Number.isFinite(hostPosition.x)
            ? hostPosition
            : { x: host.x, y: host.y },
        }
        : host,
      null,
    ),
    [host, hostPosition, position.x, position.y],
  );

  const meta = useMemo(
    () => getMilestoneMetaLabel(task, allTasks, statuses, nestedSprints, allSprints),
    [task, allTasks, statuses, nestedSprints, allSprints, anchor?.hostTaskId, anchor?.side],
  );

  const reached = meta.kind === 'reached';
  const showEstimatedDuration = isEstimatedDurationVisible(cardDisplaySettings);
  const showMeta = showEstimatedDuration || reached;

  const progressPercent = useMemo(() => {
    if (reached) return 100;
    const milestones = allTasks.filter(isMilestoneNode);
    return computeMilestoneProgressPercent(
      task,
      allTasks,
      statuses,
      nestedSprints,
      allSprints,
      milestones,
    );
  }, [task, allTasks, statuses, nestedSprints, allSprints, reached]);

  useEffect(() => {
    const el = nodeRef.current;
    if (!el || !onDimensionsChange) return undefined;

    const report = () => {
      onDimensionsChange(task.id, {
        width: MILESTONE_NODE_WIDTH,
        height: MILESTONE_NODE_HEIGHT,
      });
    };

    report();
  }, [task.id, onDimensionsChange]);

  useEffect(() => {
    const handleOpenDragMenu = (e) => {
      if (e.detail.taskId === task.id && dragHandleMenuRef.current) {
        const { handleRect } = e.detail;
        const viewportHeight = window.innerHeight;
        const menuHeight = 220;

        let top = handleRect.top;
        if (top + menuHeight > viewportHeight) {
          top = viewportHeight - menuHeight - 10;
        }

        let left = handleRect.left - 180;
        if (left < 10) {
          left = handleRect.right + 8;
        }

        dragHandleMenuRef.current.openAtPosition(left, top);
      }
    };
    window.addEventListener('openTaskDragMenu', handleOpenDragMenu);
    return () => window.removeEventListener('openTaskDragMenu', handleOpenDragMenu);
  }, [task.id]);

  const handleContextMenu = useCallback((e) => {
    const isInteractive = e.target.closest(
      'button, input, textarea, select, .task-node__drag-zone, .task-node__drag-handle, .mission-card__toolbar-item, .mission-card__name-input',
    );
    if (!isInteractive && dragHandleMenuRef.current) {
      e.preventDefault();
      e.stopPropagation();
      dragHandleMenuRef.current.openAtPosition(e.clientX, e.clientY);
    }
  }, []);

  const nodeClasses = [
    'milestone-node',
    labelOnLeft ? 'milestone-node--label-left' : 'milestone-node--label-right',
    isSelected && 'milestone-node--selected',
    isDragging && 'milestone-node--dragging',
    isAnimating && 'milestone-node--animating',
    reached && 'milestone-node--reached',
    isHoveredFromHUD && 'milestone-node--hud-hover',
    !host && 'milestone-node--unattached',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        ref={nodeRef}
        className={nodeClasses}
        data-task-id={task.id}
        style={{
          '--node-tx': `${position.x}px`,
          '--node-ty': `${position.y}px`,
          width: `${MILESTONE_NODE_WIDTH}px`,
          height: `${MILESTONE_NODE_HEIGHT}px`,
        }}
        onMouseDown={handleNodeMouseDown}
        onClick={onClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => onNodeHoverForHUD?.()}
        onMouseLeave={() => onNodeLeaveForHUD?.()}
      >
        <div className="milestone-node__frame">
          <div className="milestone-node__junction" aria-hidden="true">
            <span className="milestone-node__junction-ring" />
          </div>

          <DragHandleMenu
            ref={dragHandleMenuRef}
            task={task}
            onEditMilestone={() => setEditorOpen(true)}
            onDelete={onDelete}
            onCopy={onCopy}
            onDuplicate={onDuplicate}
          />

          <div className="milestone-node__body">
            <div className="milestone-node__flag" aria-hidden="true">
              <MilestoneFlagIcon size={20} reached={reached} />
            </div>
            <div className="milestone-node__content">
              <div className="milestone-node__title-block">
                <EditableCardName
                  task={task}
                  onRename={onRename}
                  className="milestone-node__name-field"
                />
                <ItemProgressBar
                  progress={progressPercent}
                  complete={reached}
                  className="milestone-node__progress"
                />
              </div>
              {showMeta && (
                <div className={`milestone-node__meta milestone-node__meta--${meta.kind}`}>
                  {meta.kind === 'reached' && (
                    <svg className="milestone-node__meta-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  <span>{meta.text}</span>
                </div>
              )}
              <div className="milestone-node__toolbar-wrap">
                <div className="mission-card__toolbar milestone-node__toolbar">
                  <div className="mission-card__toolbar-group">
                    <button
                      type="button"
                      className="mission-card__toolbar-item"
                      title="View path to milestone"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        openMilestonePathModal(task.id);
                      }}
                    >
                      <MilestonePathIcon size={15} />
                    </button>
                    <button
                      type="button"
                      className="mission-card__toolbar-item"
                      title="Edit milestone"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditorOpen(true);
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="mission-card__toolbar-item mission-card__toolbar-item--delete"
                      title="Delete milestone"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.();
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MilestoneEditor
        task={task}
        allTasks={allTasks}
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onRename={onRename}
        onAnchorChange={onAnchorChange}
      />
    </>
  );
};

export default MilestoneNode;
